// One-time link crypto. A random 256-bit link key goes in the URL fragment
// (browsers never send fragments to the server). With a passphrase, an
// Argon2id hash of it is mixed in, so the link alone isn't enough.
//
//   ikm = linkKey [|| Argon2id(passphrase, salt)]
//   HKDF(ikm, "link-enc")   → AES-GCM key for the item snapshot
//   HKDF(ikm, "link-proof") → proof; the server stores SHA-256(proof) and only
//                             counts a view (and returns ciphertext) for a match

import { argon2id } from "hash-wasm";
import { aesDecryptString, aesEncryptString } from "./cipher";
import { fromB64, randomBytes, toB64, utf8, type Bytes } from "./encoding";

const LINK_SALT = utf8("agile-vault-link");

function toB64Url(b: Uint8Array): string {
  return toB64(b).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
function fromB64Url(s: string): Bytes {
  const pad = s.length % 4 ? "=".repeat(4 - (s.length % 4)) : "";
  return fromB64(s.replace(/-/g, "+").replace(/_/g, "/") + pad);
}

async function passphraseBytes(passphrase: string, saltB64: string): Promise<Bytes> {
  const out = await argon2id({
    password: passphrase.trim().toLowerCase(),
    salt: fromB64(saltB64),
    iterations: 3,
    memorySize: 19_456,
    parallelism: 1,
    hashLength: 32,
    outputType: "binary",
  });
  return new Uint8Array(out);
}

async function linkSecrets(
  linkKey: Bytes,
  passphrase: string | null,
  saltB64: string,
): Promise<{ key: CryptoKey; proof: string }> {
  const ikm = passphrase
    ? new Uint8Array([...linkKey, ...(await passphraseBytes(passphrase, saltB64))])
    : linkKey;
  const base = await crypto.subtle.importKey("raw", ikm, "HKDF", false, [
    "deriveKey",
    "deriveBits",
  ]);
  const key = await crypto.subtle.deriveKey(
    { name: "HKDF", hash: "SHA-256", salt: LINK_SALT, info: utf8("link-enc") },
    base,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
  const proof = await crypto.subtle.deriveBits(
    { name: "HKDF", hash: "SHA-256", salt: LINK_SALT, info: utf8("link-proof") },
    base,
    256,
  );
  return { key, proof: toB64(new Uint8Array(proof)) };
}

async function sha256Hex(s: string): Promise<string> {
  const d = await crypto.subtle.digest("SHA-256", utf8(s));
  return [...new Uint8Array(d)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

/** Seal a payload for a new link. Returns what the server stores + the URL fragment. */
export async function sealLink(
  payload: unknown,
  passphrase: string | null,
): Promise<{ enc: string; proofHash: string; passSalt: string; fragment: string }> {
  const linkKey = randomBytes(32);
  const passSalt = passphrase ? toB64(randomBytes(16)) : "";
  const { key, proof } = await linkSecrets(linkKey, passphrase, passSalt);
  return {
    enc: await aesEncryptString(key, JSON.stringify(payload)),
    proofHash: await sha256Hex(proof),
    passSalt,
    fragment: toB64Url(linkKey),
  };
}

/** Derive the proof (to send) and key (to keep) from a fragment + passphrase. */
export async function openLinkSecrets(
  fragment: string,
  passphrase: string | null,
  passSalt: string,
): Promise<{ key: CryptoKey; proof: string }> {
  const linkKey = fromB64Url(fragment);
  if (linkKey.length !== 32) throw new Error("This link is incomplete.");
  return linkSecrets(linkKey, passphrase, passSalt);
}

export async function decryptLinkPayload<T>(key: CryptoKey, enc: string): Promise<T> {
  return JSON.parse(await aesDecryptString(key, enc)) as T;
}
